import React from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { WithColor } from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';
import { SmallLoading } from '@components/loading';
import { generateImageURL as generateImageURLAction } from '@reducers/blobStorage';
import { GlobalSearchItem, RecipeSearchItem } from '@interfaces/search';

import BlockHeader from './BlockHeader';
import LineButton from './LineButton';
import OperationsList from './OperationsList';
import { useManageRecipeBlock } from '@components/productionInformation/baseWithERP/hooks/useManageRecipeBlock';
import { SelectedLevel } from '@components/productionInformation/baseWithERP/types';

export interface RecipeBlockProps extends WithColor {
  ncFileName?: string;
  item: GlobalSearchItem | null;
  existingRecipe?: RecipeSearchItem;
  existingPart?: GlobalSearchItem | null;
  hideMoveHistoryBackButton?: boolean;
  setHistoryPool: any;
  setSelectedLevel: any;
  handleLink: (args: {
    partId?: number;
    productionOrderId?: number;
    productionOperationId?: number;
    recipeId?: number;
    recipeOperationId?: number;
  }) => void;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const RecipeBlock = ({
  C,
  ncFileName,
  item,
  existingRecipe,
  existingPart,
  hideMoveHistoryBackButton,
  setHistoryPool,
  setSelectedLevel,
  handleLink,
  scale = initialScale,
  font = initialFont,
}: RecipeBlockProps) => {
  const dispatch = useAppDispatch();
  const generateImageURL = (data: Parameters<typeof generateImageURLAction>[0]) => dispatch(generateImageURLAction(data));

  const { recipe, setRecipe, part, operations, isLoading } = useManageRecipeBlock({
    item,
    existingRecipe,
    existingPart,
  });

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <SmallLoading />
      </View>
    );
  }

  return (
    <>
      <BlockHeader
        {...{
          scale,
          font,
          C,
          documentData: existingPart ? existingPart?.documentData : item?.documentData,
          itemDescription: existingPart ? existingPart?.itemDescription : part?.partName,
          itemMemo: existingPart ? existingPart?.itemMemo : part?.description,
          itemType: 'Part',
          generateImageURL,
          hideMoveHistoryBackButton,
          onDeletePress: () => {
            setHistoryPool([{ resetPart: true, resetLevelTo: null }]);
            setSelectedLevel(null);
            setRecipe(undefined);
          },
        }}
      />
      <View style={{ marginBottom: scale(31), paddingHorizontal: scale(18) }}>
        <Text numberOfLines={1} style={[font['Title 2'], { color: C('Label/Light/Secondary'), marginBottom: scale(10) }]}>
          {i18n.t('Recipe')}
        </Text>
        <LineButton
          {...{
            scale,
            font,
            C,
            height: 66,
            bg: modalFieldBg,
            title: recipe?.recipeNumber,
            text: `Created on ${moment(recipe?.createdOn).format('D MMM YYYY')} by ${recipe?.createdBy}`,
            onAdditionalBtnPress: () => {
              setHistoryPool([]);
              setSelectedLevel(SelectedLevel.Part);
              setRecipe(undefined);
            },
            additionalBtnIcon: 'xMark',
            additionalBtnColor: 'Label/Light/Primary',
            shadow: false,
          }}
        />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          numberOfLines={1}
          style={[font['Title 2'], { color: C('Label/Light/Primary'), marginBottom: scale(10), minHeight: scale(25), paddingLeft: scale(18) }]}
        >
          {i18n.t('Select Operation')}
        </Text>
        <OperationsList
          {...{
            C,
            scale,
            font,
            operations,
            ncFileName,
            keyExtractor: (op) => String(op.recipeOperationId),
            onLinkPress: (op) =>
              handleLink({
                recipeOperationId: op.recipeOperationId,
                recipeId: recipe?.recipeId,
                partId: recipe?.partId,
              }),
          }}
        />
      </View>
    </>
  );
};

export default RecipeBlock;
